/**
 * Local-first persistence for M2: the whole workspace, debounced into
 * IndexedDB, mirrored to other tabs by a ping.
 *
 * ## What is written
 *
 * One record under a **versioned key**. The workspace is a few hundred
 * kilobytes at the scale PLAN §8.1 describes, structured-cloneable as-is, and
 * rewritten whole. Deltas belong with M3's command outbox, where there is a
 * server to send them to.
 *
 * ## When
 *
 * Debounced ~300 ms after the last change (a keystroke run in the grist table
 * therefore costs one write, not thirty), plus a synchronous-as-possible flush
 * on `visibilitychange` → hidden, which is the event that actually fires when a
 * tab is closed or backgrounded on mobile.
 *
 * ## Multi-tab
 *
 * A `BroadcastChannel` ping after every write; other tabs re-read the snapshot
 * and replace their store. This is **last-writer-wins at whole-workspace
 * granularity**: two tabs editing different recipes simultaneously will have
 * the later writer's snapshot win for both. That is knowingly coarse and
 * acceptable for a *signed-out* workspace, which is the only one it now
 * governs — a signed-in workspace gets M3's per-user Durable Object, where
 * every tab is a device and the server's command order is the truth (see
 * `src/sync/`). This ping remains the guest-mode answer, and the fast path that
 * keeps a second tab from showing stale data before the socket catches up.
 *
 * ## Identity
 *
 * The key and the channel are namespaced per user — {@link snapshotKeyFor} —
 * so guest data and each account's data are separate records that never
 * overwrite one another.
 */

import type { WorkspaceState } from '@beerwright/commands';
import {
  WORKSPACE_SCHEMA_VERSION,
  withShippedBuiltins,
  workspaceStateSchema,
} from '@beerwright/commands';
import { OUTBOX_DATABASE, OUTBOX_STORE } from '@/sync/outbox';
import type { KeyValueStore } from './idb';
import { openAppendLog, openKeyValueStore } from './idb';

/** IndexedDB database name. */
export const DATABASE_NAME = 'beerwright';

/** Object store holding whole-document snapshots. */
export const STORE_NAME = 'workspace';

/**
 * The snapshot key, versioned by the document schema.
 *
 * A schema bump therefore reads as "no saved workspace" rather than as a
 * corrupt one, and the old record stays on disk for a migration to pick up.
 *
 * This is the **guest** key. See {@link snapshotKeyFor}.
 */
export const SNAPSHOT_KEY = `snapshot.v${WORKSPACE_SCHEMA_VERSION}`;

/** Channel other tabs listen on. */
export const CHANNEL_NAME = 'beerwright.workspace';

/**
 * Where a given identity's workspace is stored.
 *
 * Signed-in workspaces are namespaced by user id; the signed-out workspace
 * keeps the bare key. That separation is what makes M3's guest rule simple and
 * honest: signing in cannot overwrite guest data, because it is not written to
 * the same place. Signing out reveals it again, unchanged.
 *
 * @param identity - The signed-in user's id, or `null` for guest.
 */
export function snapshotKeyFor(identity: string | null): string {
  return identity === null ? SNAPSHOT_KEY : `${SNAPSHOT_KEY}.${identity}`;
}

/**
 * Where a signed-in identity's **confirmed server prefix** lives.
 *
 * Beside the snapshot and namespaced the same way, but a different record and a
 * different thing: the snapshot is the optimistic workspace the screen shows,
 * this is the prefix the server has actually acknowledged, and only the second
 * can honestly carry a version number. See `sync/confirmed-store.ts`.
 *
 * Guests have none. A workspace with no server behind it has nothing to be a
 * prefix of.
 */
export function confirmedKeyFor(identity: string): string {
  return `confirmed.v${WORKSPACE_SCHEMA_VERSION}.${identity}`;
}

/**
 * Where a shared recipe's room is kept on this device (PLAN-COLLAB C1).
 *
 * A room is a small workspace of its own, so its snapshot is a record of its
 * own beside the workspace's, versioned by the same schema and namespaced by
 * the member *and* the recipe: two accounts on one browser profile can be in
 * the same room, and each keeps the copy it last saw. The room is the
 * authority, so this is only ever a head start — what lets a shared recipe
 * paint on a reload before its room has answered (D-CO11c).
 */
export function roomSnapshotKey(userId: string, recipeId: string): string {
  return `room.v${WORKSPACE_SCHEMA_VERSION}.${userId}.${recipeId}`;
}

/**
 * The cross-tab channel for an identity.
 *
 * Namespaced for the same reason as the key: a guest tab and a signed-in tab
 * are looking at different workspaces and must not reload each other's writes.
 */
export function channelNameFor(identity: string | null): string {
  return identity === null ? CHANNEL_NAME : `${CHANNEL_NAME}.${identity}`;
}

/** How long after the last change the write fires. */
export const DEBOUNCE_MS = 300;

/**
 * How long the opening read gets before the app stops waiting for it.
 *
 * Reading the snapshot is the one storage operation with a person waiting on
 * it: it gates `hydrated`, and `AppShell` shows the opening splash until that
 * flips. Everything else here is behind a debounce and reports its failures
 * through `onError`. So this read, and only this read, needs a floor under how
 * badly IndexedDB can behave — see `WorkspaceBinding` in `workspace-context.tsx`,
 * which is where the race is run.
 *
 * The failure it exists for is not a slow disk. Chromium can leave an
 * `indexedDB.open()` request **unsettled** — no `success`, no `error`, not even
 * `blocked` — when a document is torn down while the next one is opening the
 * same database, which is exactly what a burst of rapid same-origin navigations
 * produces. `idb.ts` memoises that open, so the promise the whole layer awaits
 * simply never settles: no error to report, no rejection to catch, nothing to
 * retry. Unbounded, a local-first app then sits forever on a splash screen,
 * having failed at the one thing being local-first was supposed to guarantee.
 *
 * Four seconds sits far outside any real read — a few hundred kilobytes off a
 * warm IndexedDB is single-digit milliseconds, and a cold cache on a tired
 * phone is tens — while staying inside what someone will sit through rather
 * than reload. Giving up costs no data: the same stalled connection blocks the
 * writes, so the saved record is still on disk, untouched, for the next boot to
 * read.
 *
 * It is deliberately **shorter** than `OPEN_TIMEOUT_MS` in `idb.ts` (10 s),
 * which bounds the stalled open itself. The two are one mechanism read at two
 * distances: this number decides how long a person looks at the splash, that one
 * decides how long everything behind the splash — the writer, the sync outbox,
 * and so the socket that refills a signed-in workspace — stays stuck before it
 * fails and retries. The splash must be the first to give up, so it is.
 */
export const HYDRATION_TIMEOUT_MS = 4_000;

/** What lands in IndexedDB. */
export interface Snapshot {
  readonly schemaVersion: number;
  /** Wall clock at write time; diagnostic only, nothing reads it to resolve conflicts. */
  readonly savedAt: number;
  readonly state: WorkspaceState;
}

/** The persistence handle a store is wired to. */
export interface Persistence {
  /** Read the saved workspace, or `null` when there is none (or it is unreadable). */
  load: () => Promise<WorkspaceState | null>;
  /**
   * Note a change; schedules a debounced write.
   *
   * `mark` is an opaque, monotonically increasing position the caller may use
   * to say "everything up to here is in this state". It is handed straight back
   * to {@link Persistence.onWritten} once the write commits, which is what lets
   * a caller know which of its own commands the snapshot on disk now contains.
   */
  schedule: (state: WorkspaceState, mark?: number) => void;
  /** Write immediately, cancelling any pending debounce. */
  flush: () => Promise<void>;
  /** Called when *another* tab wrote. */
  onExternalChange: (listener: () => void) => () => void;
  /** Called with the scheduled `mark` after a write of this layer's commits. */
  onWritten: (listener: (mark: number) => void) => () => void;
  /** Drop timers, listeners and the channel. */
  dispose: () => void;
}

/** Options for {@link createPersistence}. */
export interface PersistenceOptions {
  /** Storage backend. Defaults to IndexedDB; tests pass an in-memory one. */
  readonly store?: KeyValueStore;
  readonly key?: string;
  readonly debounceMs?: number;
  readonly channelName?: string | null;
  readonly now?: () => number;
  /** Where write failures go. Defaults to `console.warn`. */
  readonly onError?: (error: unknown) => void;
}

/** A `BroadcastChannel`-shaped thing, so tests can stub it. */
interface ChannelLike {
  postMessage: (message: unknown) => void;
  close: () => void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

/** The ping's payload. `writerId` is what stops a tab reloading its own write. */
interface ChangePing {
  readonly type: 'workspace-changed';
  readonly writerId: string;
}

function openChannel(name: string | null): ChannelLike | null {
  if (name === null) return null;
  const ctor = (globalThis as { BroadcastChannel?: typeof BroadcastChannel }).BroadcastChannel;
  if (ctor === undefined) return null;
  return new ctor(name) as unknown as ChannelLike;
}

/**
 * Wire a storage backend, a debounce and a cross-tab channel together.
 *
 * Every failure path is non-fatal: a browser in private mode with IndexedDB
 * disabled degrades to an in-memory session rather than a blank screen, because
 * losing a recipe is worse than losing persistence.
 */
/**
 * Erase everything this device holds for one identity.
 *
 * The local half of account deletion (PLAN §8.4): the server can wipe the
 * Durable Object and the D1 rows, but the browser's own copy of the workspace
 * and its unsent outbox are beyond its reach, and leaving them would mean a
 * deleted account's recipes were still sitting on the machine.
 *
 * Both stores are cleared even if one throws — a failure to clear the outbox
 * must not leave the snapshot behind as well.
 *
 * @param identity - The signed-in user's id. The guest workspace lives under a
 *   different key and is deliberately untouched: it was never part of the
 *   account.
 * @param roomIds - The shared recipes this account is in, whose room snapshots
 *   go too. Named by the caller because the store cannot list its keys.
 */
export async function eraseLocalWorkspace(
  identity: string,
  roomIds: readonly string[] = [],
): Promise<void> {
  const snapshots = openKeyValueStore(DATABASE_NAME, STORE_NAME);
  const results = await Promise.allSettled([
    snapshots.delete(snapshotKeyFor(identity)),
    // The confirmed prefix is this account's data too, and a resumed prefix
    // for an account whose snapshot has gone would rebuild it on the next
    // sign-in.
    snapshots.delete(confirmedKeyFor(identity)),
    ...roomIds.map((recipeId) => snapshots.delete(roomSnapshotKey(identity, recipeId))),
    clearOutboxFor(identity),
  ]);
  for (const result of results) {
    if (result.status === 'rejected') {
      console.warn('[beerwright] could not clear local storage:', result.reason);
    }
  }
}

/**
 * Remove this user's queued commands, their rooms' included (a room's queue is
 * keyed `` `${userId}:room:${recipeId}` ``). Other identities' are left alone.
 */
async function clearOutboxFor(identity: string): Promise<void> {
  const log = openAppendLog(OUTBOX_DATABASE, OUTBOX_STORE);
  const rows = await log.all<{ owner?: string }>();
  const mine = (owner: string | undefined) =>
    owner === identity || owner?.startsWith(`${identity}:room:`) === true;
  await log.remove(rows.filter((row) => mine(row.value?.owner)).map((row) => row.key));
}

export function createPersistence(options: PersistenceOptions = {}): Persistence {
  const store = options.store ?? openKeyValueStore(DATABASE_NAME, STORE_NAME);
  const key = options.key ?? SNAPSHOT_KEY;
  const debounceMs = options.debounceMs ?? DEBOUNCE_MS;
  const now = options.now ?? Date.now;
  const onError =
    options.onError ??
    ((error: unknown) => {
      console.warn('[beerwright] persistence:', error);
    });

  const writerId = Math.random().toString(36).slice(2);
  const channel = openChannel(
    options.channelName === undefined ? CHANNEL_NAME : options.channelName,
  ); // prettier-ignore
  const listeners = new Set<() => void>();
  const writtenListeners = new Set<(mark: number) => void>();

  let pending: { state: WorkspaceState; mark: number } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> = Promise.resolve();

  if (channel !== null) {
    channel.onmessage = (event) => {
      const data = event.data as Partial<ChangePing> | null;
      if (data?.type !== 'workspace-changed' || data.writerId === writerId) return;
      for (const listener of listeners) listener();
    };
  }

  async function write(state: WorkspaceState, mark: number): Promise<void> {
    const snapshot: Snapshot = { schemaVersion: WORKSPACE_SCHEMA_VERSION, savedAt: now(), state };
    try {
      await store.set(key, snapshot);
      // Only after the write commits: a caller pruning what it holds on the
      // strength of this must not prune ahead of the disk.
      for (const listener of writtenListeners) listener(mark);
      channel?.postMessage({ type: 'workspace-changed', writerId } satisfies ChangePing);
    } catch (error) {
      onError(error);
    }
  }

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  return {
    async load() {
      try {
        const snapshot = await store.get<Snapshot>(key);
        if (snapshot === undefined) return null;
        // Validated rather than trusted: the record may have been written by an
        // older build, hand-edited in devtools, or half-migrated.
        const parsed = workspaceStateSchema.safeParse(snapshot.state);
        if (!parsed.success) {
          onError(new Error('Saved workspace failed validation; starting fresh.'));
          return null;
        }
        return withShippedBuiltins(parsed.data);
      } catch (error) {
        onError(error);
        return null;
      }
    },

    schedule(state, mark = 0) {
      pending = { state, mark };
      clearTimer();
      timer = setTimeout(() => {
        timer = null;
        const next = pending;
        pending = null;
        if (next !== null) inFlight = write(next.state, next.mark);
      }, debounceMs);
    },

    async flush() {
      clearTimer();
      const next = pending;
      pending = null;
      if (next !== null) inFlight = write(next.state, next.mark);
      await inFlight;
    },

    onExternalChange(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    onWritten(listener) {
      writtenListeners.add(listener);
      return () => {
        writtenListeners.delete(listener);
      };
    },

    dispose() {
      clearTimer();
      listeners.clear();
      writtenListeners.clear();
      channel?.close();
    },
  };
}
